import React from 'react'
import { Button, Input, Label, Switch } from '@/shared/ui'
import { Table, Thead, Tbody, Tr, Th, Td } from '@/shared/ui/Table'
import { useToast } from '@/shared/ui/Toast'
import { listAffiliateInvites, createAffiliateInvite, deleteAffiliateInvite, regenerateAffiliateInvite, updateAffiliateInvite, type AffiliateInvite } from '../services/affiliateInviteService'

type Props = {}

export default function AffiliateInvitesPanel(_: Props) {
  const { add: toast } = useToast()
  const [items, setItems] = React.useState<AffiliateInvite[]>([])
  const [links, setLinks] = React.useState<Array<{ id: string; link: string }>>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [form, setForm] = React.useState<{ email: string; code: string; adv: boolean; mode: 'percent'|'fixed'; value: string; recurring: boolean; expires_at: string }>
    ({ email: '', code: '', adv: false, mode: 'percent', value: '10', recurring: true, expires_at: '' })

  async function load() {
    setLoading(true); setError(null)
    try {
      const { items, links } = await listAffiliateInvites()
      setItems(items); setLinks(links)
    } catch (e: any) { setError(e?.message || 'Falha ao carregar convites') }
    finally { setLoading(false) }
  }
  React.useEffect(()=>{ load() },[])

  function linkOf(id: string): string | null { return links.find(l => l.id === id)?.link || null }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault()
    try {
      const email = (form.email || '').trim().toLowerCase()
      if (!/.+@.+\..+/.test(email)) throw new Error('E-mail inválido')
      const payload: any = { email }
      if (form.code.trim()) payload.code = form.code.trim()
      if (form.adv) {
        payload.commission_mode = form.mode
        payload.commission_value = Number(form.value) || 0
        payload.recurrence = form.recurring ? 'recurring' : 'one_time'
        if (form.expires_at.trim()) payload.expires_at = new Date(form.expires_at).toISOString()
      }
      await createAffiliateInvite(payload)
      setForm({ email: '', code: '', adv: false, mode: 'percent', value: '10', recurring: true, expires_at: '' })
      await load()
      toast({ title: 'Convite criado', description: 'Link gerado com sucesso', variant: 'success', soft: true })
    } catch (e:any) {
      toast({ title: 'Erro', description: e?.message || 'Falha ao criar convite', variant: 'error', soft: true })
    }
  }

  async function onToggleActive(it: AffiliateInvite) {
    try { await updateAffiliateInvite(it.id, { active: !it.active }); await load() } catch (e:any) {
      toast({ title: 'Erro', description: e?.message || 'Falha ao atualizar', variant: 'error', soft: true })
    }
  }
  async function onRegenerate(it: AffiliateInvite) {
    try {
      const resp = await regenerateAffiliateInvite(it.id)
      await load()
      const link = resp?.link
      if (link) { await navigator.clipboard?.writeText(link).catch(()=>{}); toast({ title: 'Link regenerado', description: 'Copiado para a área de transferência', variant: 'success', soft: true }) }
    } catch (e:any) {
      toast({ title: 'Erro', description: e?.message || 'Falha ao regenerar', variant: 'error', soft: true })
    }
  }
  async function onDelete(it: AffiliateInvite) {
    if (!confirm('Remover este convite?')) return
    try { await deleteAffiliateInvite(it.id); await load() } catch (e:any) {
      toast({ title: 'Erro', description: e?.message || 'Falha ao remover', variant: 'error', soft: true })
    }
  }

  return (
    <div className="rounded-md border border-border p-4">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Convites de Afiliado</h2>
        <p className="text-sm text-foreground/70">Gere links exclusivos para cadastro de afiliados por e‑mail autorizado.</p>
      </div>

      <form onSubmit={onCreate} className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-4">
        <div>
          <Label className="mb-1 block">E-mail</Label>
          <Input value={form.email} onChange={(e)=> setForm({ ...form, email: e.target.value })} placeholder="afiliado@dominio.com" required />
        </div>
        <div>
          <Label className="mb-1 block">Código sugerido (opcional)</Label>
          <Input value={form.code} onChange={(e)=> setForm({ ...form, code: e.target.value })} placeholder="ex.: nome-xyz1" />
        </div>
        <div className="flex items-end gap-2">
          <Switch checked={form.adv} onChange={(e: any)=> setForm({ ...form, adv: !!e?.target?.checked })} />
          <span className="text-sm">Avançado (comissão, expiração)</span>
        </div>
        <div className="flex items-end justify-end">
          <Button type="submit">Criar convite</Button>
        </div>
        {form.adv && (
          <>
            <div>
              <Label className="mb-1 block">Modo</Label>
              <select className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.mode} onChange={(e)=> setForm({ ...form, mode: e.target.value as any })}>
                <option value="percent">Percentual (%)</option>
                <option value="fixed">Valor fixo</option>
              </select>
            </div>
            <div>
              <Label className="mb-1 block">Valor</Label>
              <Input type="number" step={form.mode==='percent'?'0.1':'0.01'} value={form.value} onChange={(e)=> setForm({ ...form, value: e.target.value })} placeholder={form.mode==='percent'?'10':'49.90'} />
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={form.recurring} onChange={(e: any)=> setForm({ ...form, recurring: !!e?.target?.checked })} />
              <span className="text-sm">Recorrente (a cada renovação)</span>
            </div>
            <div>
              <Label className="mb-1 block">Expira em (opcional)</Label>
              <Input type="datetime-local" value={form.expires_at} onChange={(e)=> setForm({ ...form, expires_at: e.target.value })} />
            </div>
          </>
        )}
      </form>

      <div className="rounded-md">
        <Table className="min-w-full">
          <Thead>
            <Tr>
              <Th>E-mail</Th>
              <Th>Código</Th>
              <Th>Comissão</Th>
              <Th>Token/Link</Th>
              <Th>Status</Th>
              <Th>Expira</Th>
              <Th className="text-right">Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading && (<Tr><Td colSpan={7} className="text-center">Carregando...</Td></Tr>)}
            {error && !loading && (<Tr><Td colSpan={7} className="text-center text-destructive">{error}</Td></Tr>)}
            {!loading && !items.length && !error && (<Tr><Td colSpan={7} className="text-center text-foreground/70">Nenhum convite.</Td></Tr>)}
            {items.map(it => (
              <Tr key={it.id}>
                <Td>{it.email}</Td>
                <Td>{it.code || '-'}</Td>
                <Td>{it.commission_mode ? (it.commission_mode==='percent' ? `${Number(it.commission_value||0).toFixed(1)}%` : (Number(it.commission_value||0)).toLocaleString('pt-BR',{ style:'currency', currency: 'BRL' })) : '-'}</Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{(it.token||'').slice(0,10)}…</code>
                    <Button variant="outline" onClick={async ()=> { const link = linkOf(it.id); if (link) { await navigator.clipboard?.writeText(link).catch(()=>{}); toast({ title: 'Link copiado', description: link, soft: true }) } }}>Copiar link</Button>
                  </div>
                </Td>
                <Td>{it.used_at ? 'Usado' : (it.active ? 'Ativo' : 'Inativo')}</Td>
                <Td>{it.expires_at ? new Date(it.expires_at).toLocaleString() : '-'}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={()=> onToggleActive(it)} disabled={!!it.used_at}>{it.active ? 'Desativar' : 'Ativar'}</Button>
                    <Button variant="outline" onClick={()=> onRegenerate(it)} disabled={!!it.used_at}>Regenerar</Button>
                    <Button variant="destructive" onClick={()=> onDelete(it)} disabled={!!it.used_at}>Remover</Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
    </div>
  )
}

